/** <lazy-video>: wraps one section <video> whose <source> carry `data-src`, hydrated as it approaches the viewport then paused off screen (a `<video autoplay preload="auto">` downloaded ~9 MB on the home page, even when hidden by media query), and a pause coming from <video-controls> is never overridden on the way back in. As a custom element, every insertion (infinite scroll, Depict, HTML cache restore, colour switch) is picked up by connectedCallback. Attributes: `autoplay`, `margin` (IntersectionObserver rootMargin). */
(function () {
  if (customElements.get("lazy-video")) return;

  // Generous margin: the video is ready before it is really visible, so the overlaid <video-controls> never deal with a source-less element.
  const ROOT_MARGIN = "400px 0px";

  // State kept in JS, not in attributes: a serialised copy of the markup (infinite-scroll HTML cache) must not carry it over.
  const hydrated = new WeakSet();
  const pausedByUs = new WeakSet();

  function wantsAutoplay(video) {
    const host = video.closest("lazy-video");
    return !!host && host.hasAttribute("autoplay");
  }

  /** Pose les `src` des <source> ; `data-src` est conservé pour qu'une copie du markup reste réhydratable. */
  function hydrate(video) {
    if (hydrated.has(video)) return false;
    hydrated.add(video);

    video.querySelectorAll("source[data-src]").forEach((source) => {
      source.src = source.dataset.src;
    });
    // Obligatoire : modifier les <source> après coup n'a aucun effet tant que
    // load() n'a pas relancé la sélection de ressource.
    video.load();
    return true;
  }

  /** New element built from already hydrated markup (HTML cache, clone): back to its lazy state, otherwise a `preload="auto"` video would download straight away. */
  function reset(video) {
    video.querySelectorAll("source[data-src][src]").forEach((source) => source.removeAttribute("src"));
    delete video.dataset.autoplayRefused;
  }

  // Videos the browser refused to autoplay. iOS Low Power Mode (and Android data saver) block autoplay outright, but still allow play() called from a user gesture: the first tap or key press on the page retries them, otherwise a banner with no <video-controls> would never start at all.
  const refused = new Set();
  const GESTURE_EVENTS = ["pointerdown", "keydown"];
  let gestureArmed = false;

  function play(video) {
    const promise = video.play();
    if (!promise || typeof promise.catch !== "function") return;
    promise.catch(() => armGestureRetry(video));
  }

  // Flag read by the CSS to reveal a fallback play button on sections that render no <video-controls>: a refused autoplay would otherwise leave the poster with no affordance at all. Cleared as soon as playback starts, whoever started it, so a transient rejection (source swap, element detached) heals itself.
  function markRefused(video) {
    if (video.dataset.autoplayRefused === "true") return;
    video.dataset.autoplayRefused = "true";
    video.addEventListener("play", () => delete video.dataset.autoplayRefused, { once: true });
  }

  function armGestureRetry(video) {
    refused.add(video);
    markRefused(video);
    if (gestureArmed) return;
    gestureArmed = true;
    GESTURE_EVENTS.forEach((type) => {
      document.addEventListener(type, onGesture, { passive: true, capture: true });
    });
  }

  function onGesture(event) {
    // A gesture on the controls belongs to <video-controls>: starting the video here on pointerdown would make its click handler read the video as playing and toggle it straight back off.
    const target = event && event.target;
    if (target && target.closest && target.closest("video-controls")) return;

    refused.forEach((video) => {
      // Detached since (card replaced, filters applied): drop it.
      if (!video.isConnected) {
        refused.delete(video);
        return;
      }
      // Paused off screen by the observer, or autoplay turned off since: leave it alone.
      if (!wantsAutoplay(video) || pausedByUs.has(video)) return;

      // The tap is following the link wrapping this very video: it is a navigation, not a request to play. Starting playback here is what makes a tap on the media look like it both launches the video and opens the link; the fallback play button is the affordance for that case.
      const wrapper = video.closest("a[href]");
      if (wrapper && target && wrapper.contains(target)) return;

      const promise = video.play();
      if (!promise || typeof promise.then !== "function") return;
      promise
        .then(() => {
          refused.delete(video);
          if (!refused.size) disarmGestureRetry();
        })
        .catch(() => {});
    });
    if (!refused.size) disarmGestureRetry();
  }

  function disarmGestureRetry() {
    if (!gestureArmed) return;
    gestureArmed = false;
    GESTURE_EVENTS.forEach((type) => {
      document.removeEventListener(type, onGesture, { capture: true });
    });
  }

  function onIntersect(entries) {
    entries.forEach((entry) => {
      const video = entry.target;

      if (!entry.isIntersecting) {
        // Hors écran : on coupe le décodage. Le drapeau distingue notre pause
        // de celle de l'utilisateur.
        if (!video.paused) {
          video.pause();
          pausedByUs.add(video);
        }
        return;
      }

      const justHydrated = hydrate(video);
      const shouldResume = pausedByUs.has(video);

      if (wantsAutoplay(video) && (justHydrated || shouldResume)) {
        pausedByUs.delete(video);
        play(video);
      }
    });
  }

  // One observer per rootMargin, shared by the whole page. The PDP slider asks for a wider one: its next slides are clipped by the horizontal scroller, so a vertical-only margin never reaches them.
  const observers = new Map();

  function observerFor(margin) {
    let observer = observers.get(margin);
    if (!observer) {
      observer = new IntersectionObserver(onIntersect, { rootMargin: margin });
      observers.set(margin, observer);
    }
    return observer;
  }

  class LazyVideo extends HTMLElement {
    #video = null;
    #observer = null;

    connectedCallback() {
      // Resolved and reset on the first connection only: Depict moves cards around, and a reconnection must not reset a playing video.
      if (!this.#video) {
        this.#video = this.querySelector(":scope > video");
        if (!this.#video) return;
        reset(this.#video);
      }

      // Pas d'IntersectionObserver (très vieux navigateur) : on hydrate tout,
      // comportement identique à l'ancien `autoplay preload="auto"`.
      if (!("IntersectionObserver" in window)) {
        if (hydrate(this.#video) && wantsAutoplay(this.#video)) play(this.#video);
        return;
      }

      this.#observer = observerFor(this.getAttribute("margin") || ROOT_MARGIN);
      this.#observer.observe(this.#video);
    }

    disconnectedCallback() {
      if (!this.#observer || !this.#video) return;
      this.#observer.unobserve(this.#video);
      // The browser pauses a detached media element: counted as our pause, so a reinsertion resumes it.
      if (!this.#video.paused) pausedByUs.add(this.#video);
    }
  }

  customElements.define("lazy-video", LazyVideo);

  // Former API, kept as a no-op for any caller still around: insertions are now picked up by connectedCallback.
  window.lazyVideo = { refresh() {} };
})();
